// components/Survey/surveyUtils.js — question model, validation, analytics and export helpers
import XLSX from "xlsx-js-style";
import { generateId } from "../../services/surveyService";

export const OTHER_ID = "__other__";

// ── Question types ─────────────────────────────────────────────────────────────

export const QUESTION_TYPES = [
  { type: "short_text",    label: "Short answer",     group: "Text",    desc: "A single line of text" },
  { type: "long_text",     label: "Paragraph",        group: "Text",    desc: "Longer free-text feedback" },
  { type: "single_choice", label: "Multiple choice",  group: "Choice",  desc: "Pick exactly one option" },
  { type: "multi_choice",  label: "Checkboxes",       group: "Choice",  desc: "Pick one or more options" },
  { type: "dropdown",      label: "Dropdown",         group: "Choice",  desc: "Pick one from a compact list" },
  { type: "yes_no",        label: "Yes / No",         group: "Choice",  desc: "A simple binary question" },
  { type: "rating",        label: "Star rating",      group: "Scale",   desc: "Rate from 1 to 5 (or 10) stars" },
  { type: "scale",         label: "Linear scale",     group: "Scale",   desc: "A numbered scale with end labels" },
  { type: "nps",           label: "Net Promoter",     group: "Scale",   desc: "0–10 likelihood to recommend" },
  { type: "likert",        label: "Likert matrix",    group: "Scale",   desc: "Several statements on one agreement scale" },
  { type: "number",        label: "Number",           group: "Other",   desc: "A numeric value" },
  { type: "date",          label: "Date",             group: "Other",   desc: "A calendar date" },
  { type: "section",       label: "Section heading",  group: "Other",   desc: "Group questions under a heading" },
];

export const TYPE_META = Object.fromEntries(QUESTION_TYPES.map((t) => [t.type, t]));
export const CHOICE_TYPES = ["single_choice", "multi_choice", "dropdown"];
export const SCORED_TYPES = ["rating", "scale", "nps"];

export const AGREEMENT_SCALE = ["Strongly disagree", "Disagree", "Neutral", "Agree", "Strongly agree"];

function opts(labels) {
  return labels.map((label) => ({ id: generateId("o"), label }));
}

export function newQuestion(type) {
  const base = { id: generateId("q"), type, title: "", description: "", required: type !== "section" };
  switch (type) {
    case "single_choice":
    case "multi_choice":
    case "dropdown":
      return { ...base, options: opts(["Option 1", "Option 2", "Option 3"]), allowOther: false };
    case "rating":
      return { ...base, ratingMax: 5 };
    case "scale":
      return { ...base, scaleMin: 1, scaleMax: 5, minLabel: "Poor", maxLabel: "Excellent" };
    case "nps":
      return { ...base, title: "How likely are you to recommend LekOil as a place to work?", minLabel: "Not at all likely", maxLabel: "Extremely likely" };
    case "likert":
      return { ...base, rows: opts(["Statement 1", "Statement 2"]), columns: opts(AGREEMENT_SCALE) };
    case "number":
      return { ...base, min: "", max: "" };
    case "section":
      return { ...base, required: false };
    default:
      return base;
  }
}

// Re-key every id so a duplicated question / survey never collides with its source
export function cloneQuestion(q) {
  const copy = { ...q, id: generateId("q") };
  if (q.options) copy.options = q.options.map((o) => ({ ...o, id: generateId("o") }));
  if (q.rows) copy.rows = q.rows.map((o) => ({ ...o, id: generateId("o") }));
  if (q.columns) copy.columns = q.columns.map((o) => ({ ...o, id: generateId("o") }));
  return copy;
}

export function cloneQuestions(questions) {
  // Keep showIf links pointing at the copies
  const idMap = {};
  const optMap = {};
  const copies = questions.map((q) => {
    const c = cloneQuestion(q);
    idMap[q.id] = c.id;
    (q.options || []).forEach((o, i) => { optMap[o.id] = c.options[i].id; });
    return c;
  });
  return copies.map((c) => (c.showIf
    ? { ...c, showIf: { questionId: idMap[c.showIf.questionId] || c.showIf.questionId, equals: optMap[c.showIf.equals] || c.showIf.equals } }
    : c));
}

export function scaleRange(q) {
  if (q.type === "nps") return [0, 10];
  if (q.type === "rating") return [1, Number(q.ratingMax) || 5];
  return [Number(q.scaleMin ?? 1), Number(q.scaleMax ?? 5)];
}

export function countQuestions(questions = []) {
  return questions.filter((q) => q.type !== "section").length;
}

export function estimateMinutes(questions = []) {
  const secs = questions.reduce((sum, q) => {
    if (q.type === "section") return sum;
    if (q.type === "long_text") return sum + 60;
    if (q.type === "likert") return sum + 8 * (q.rows?.length || 1);
    return sum + 15;
  }, 0);
  return Math.max(1, Math.round(secs / 60));
}

// ── Templates ──────────────────────────────────────────────────────────────────

function q(type, title, extra = {}) {
  return { ...newQuestion(type), title, ...extra };
}

export const SURVEY_TEMPLATES = [
  {
    key: "blank",
    name: "Blank survey",
    desc: "Start from scratch",
    build: () => ({ title: "", description: "", category: "", questions: [q("short_text", "")] }),
  },
  {
    key: "engagement",
    name: "Employee engagement",
    desc: "Satisfaction, wellbeing and eNPS",
    build: () => ({
      title: "Employee Engagement Survey",
      description: "Tell us honestly how you feel about working at LekOil. Your answers help leadership prioritise improvements.",
      category: "Engagement",
      questions: [
        q("section", "About your role"),
        q("rating", "Overall, how satisfied are you with your job?"),
        q("likert", "How much do you agree with the following?", {
          rows: opts([
            "I understand how my work contributes to company goals",
            "I have the tools and resources to do my job well",
            "My manager gives me regular, useful feedback",
            "I feel recognised for good work",
            "I see a path for growth at LekOil",
          ]),
          columns: opts(AGREEMENT_SCALE),
        }),
        q("section", "Wellbeing"),
        q("scale", "How manageable is your current workload?", { scaleMin: 1, scaleMax: 5, minLabel: "Overwhelming", maxLabel: "Very manageable" }),
        q("yes_no", "Do you feel safe raising concerns at work?"),
        q("nps", "How likely are you to recommend LekOil as a place to work?"),
        q("long_text", "What one change would most improve your experience at work?", { required: false }),
      ],
    }),
  },
  {
    key: "training",
    name: "Training feedback",
    desc: "Evaluate a session or KSS",
    build: () => ({
      title: "Training Session Feedback",
      description: "Help us improve future sessions by rating the training you attended.",
      category: "Training",
      questions: [
        q("short_text", "Which session did you attend?"),
        q("rating", "How would you rate the session overall?"),
        q("likert", "Rate the following aspects", {
          rows: opts(["Content was relevant to my role", "Presenter was clear and engaging", "Session length was appropriate"]),
          columns: opts(AGREEMENT_SCALE),
        }),
        q("single_choice", "How will you apply what you learned?", { options: opts(["Immediately in my daily work", "In an upcoming project", "Not sure yet", "Not applicable to my role"]) }),
        q("long_text", "Any topics you would like covered in future?", { required: false }),
      ],
    }),
  },
  {
    key: "safety",
    name: "HSE pulse check",
    desc: "Quick safety culture pulse",
    build: () => {
      const seen = q("yes_no", "In the last month, have you observed an unsafe act or condition?");
      return {
        title: "HSE Pulse Check",
        description: "A short, regular check on safety culture across our sites and offices.",
        category: "HSE",
        questions: [
          q("scale", "How safe do you feel in your work environment?", { scaleMin: 1, scaleMax: 5, minLabel: "Not safe", maxLabel: "Very safe" }),
          seen,
          q("single_choice", "Did you report it?", { options: opts(["Yes, and it was resolved", "Yes, but no action yet", "No"]), showIf: { questionId: seen.id, equals: "yes" } }),
          q("multi_choice", "Which areas need the most attention?", { options: opts(["PPE availability", "Housekeeping", "Driving & logistics", "Emergency preparedness", "Ergonomics"]), allowOther: true, required: false }),
          q("long_text", "Suggestions to improve safety", { required: false }),
        ],
      };
    },
  },
  {
    key: "service",
    name: "Internal service feedback",
    desc: "Rate a department's support",
    build: () => ({
      title: "SCM Internal Customer Satisfaction",
      description: "Rate the support you receive from the Supply Chain Management team.",
      category: "Service",
      questions: [
        q("dropdown", "Which department are you in?", { options: opts(["Operations", "Finance", "HSE", "HR", "Technical", "Other"]) }),
        q("rating", "How satisfied are you with SCM's responsiveness?"),
        q("rating", "How satisfied are you with the quality of goods and services delivered?"),
        q("scale", "How easy is it to raise a request with SCM?", { scaleMin: 1, scaleMax: 5, minLabel: "Very difficult", maxLabel: "Very easy" }),
        q("long_text", "What should SCM start, stop or continue doing?", { required: false }),
      ],
    }),
  },
];

// ── Visibility & validation ────────────────────────────────────────────────────

export function isVisible(question, answers) {
  const cond = question.showIf;
  if (!cond || !cond.questionId) return true;
  const v = answers[cond.questionId];
  if (Array.isArray(v)) return v.includes(cond.equals);
  return v === cond.equals;
}

export function hasAnswer(question, value) {
  if (value === undefined || value === null || value === "") return false;
  if (Array.isArray(value)) return value.length > 0;
  if (question.type === "likert") return typeof value === "object" && Object.keys(value).length > 0;
  return true;
}

export function validateAnswers(questions, answers) {
  const errors = {};
  for (const q of questions) {
    if (q.type === "section" || !isVisible(q, answers)) continue;
    const v = answers[q.id];
    if (q.required) {
      if (q.type === "likert") {
        const missing = (q.rows || []).filter((r) => !v?.[r.id]);
        if (missing.length) { errors[q.id] = `Please rate all ${q.rows.length} statements`; continue; }
      } else if (!hasAnswer(q, v)) {
        errors[q.id] = "This question is required";
        continue;
      }
    }
    const otherSelected = v === OTHER_ID || (Array.isArray(v) && v.includes(OTHER_ID));
    if (otherSelected && !String(answers[`${q.id}${OTHER_ID}`] || "").trim()) {
      errors[q.id] = "Please describe your “Other” answer";
      continue;
    }
    if (q.type === "number" && hasAnswer(q, v)) {
      const n = Number(v);
      if (Number.isNaN(n)) errors[q.id] = "Enter a valid number";
      else if (q.min !== "" && q.min !== undefined && n < Number(q.min)) errors[q.id] = `Must be at least ${q.min}`;
      else if (q.max !== "" && q.max !== undefined && n > Number(q.max)) errors[q.id] = `Must be at most ${q.max}`;
    }
  }
  return errors;
}

// Drop answers to questions hidden by skip logic so stale values never reach analytics
export function pruneHiddenAnswers(questions, answers) {
  const out = {};
  for (const q of questions) {
    if (q.type === "section" || !isVisible(q, answers)) continue;
    if (answers[q.id] !== undefined) out[q.id] = answers[q.id];
    const otherKey = `${q.id}${OTHER_ID}`;
    if (answers[otherKey]) out[otherKey] = answers[otherKey];
  }
  return out;
}

// Problems that should block publishing a survey
export function validateSurveyDefinition(survey) {
  const problems = [];
  if (!survey.title?.trim()) problems.push("Give the survey a title");
  const qs = survey.questions || [];
  if (countQuestions(qs) === 0) problems.push("Add at least one question");
  qs.forEach((q, i) => {
    if (q.type === "section") return;
    const n = `Question ${qs.slice(0, i + 1).filter((x) => x.type !== "section").length}`;
    if (!q.title?.trim()) problems.push(`${n} has no question text`);
    if (CHOICE_TYPES.includes(q.type) && (q.options || []).filter((o) => o.label.trim()).length < 2) problems.push(`${n} needs at least two options`);
    if (q.type === "likert" && (!(q.rows || []).length || (q.columns || []).length < 2)) problems.push(`${n} needs statements and at least two scale points`);
    if (q.type === "scale" && Number(q.scaleMax) <= Number(q.scaleMin)) problems.push(`${n} scale maximum must be greater than minimum`);
  });
  return problems;
}

// ── Assignment status ──────────────────────────────────────────────────────────

export const STATUS_META = {
  draft:     { label: "Draft",     color: "#605E5C", bg: "#F3F2F1" },
  active:    { label: "Active",    color: "#0B6A0B", bg: "#E7F4E4" },
  closed:    { label: "Closed",    color: "#7A3A00", bg: "#FFF1E5" },
  pending:   { label: "Pending",   color: "#005A9E", bg: "#E3F0FF" },
  overdue:   { label: "Overdue",   color: "#A4262C", bg: "#FDE7E9" },
  submitted: { label: "Submitted", color: "#0B6A0B", bg: "#E7F4E4" },
  expired:   { label: "Closed",    color: "#605E5C", bg: "#F3F2F1" },
};

export function todayIso() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

export function isSurveyOpen(survey) {
  if (!survey || survey.status !== "active") return false;
  const closes = survey.settings?.closesAt;
  return !closes || closes >= todayIso();
}

export function assignmentStatus(assignment, response, survey) {
  if (response) return "submitted";
  if (!isSurveyOpen(survey)) return "expired";
  if (assignment.dueDate && assignment.dueDate < todayIso()) return "overdue";
  return "pending";
}

// ── Response selection ─────────────────────────────────────────────────────────

/**
 * mode: "latest" — newest submission per respondent (default for analysis)
 *       "all"    — every submission across rounds
 *       number   — only that round
 */
export function selectResponses(responses, { mode = "latest", department = "" } = {}) {
  // One response per assignment — guards against a double submit from two tabs
  const seen = new Set();
  let list = [...responses]
    .sort((a, b) => (a.submittedAt < b.submittedAt ? -1 : 1))
    .filter((r) => (seen.has(r.assignmentId) ? false : seen.add(r.assignmentId)));
  if (department) list = list.filter((r) => (r.department || "Unspecified") === department);
  if (mode === "latest") {
    const byEmail = new Map();
    list.forEach((r) => byEmail.set(r.email, r));
    list = [...byEmail.values()];
  } else if (typeof mode === "number") {
    list = list.filter((r) => r.round === mode);
  }
  return list.sort((a, b) => (a.submittedAt < b.submittedAt ? 1 : -1));
}

// ── Per-question analytics ─────────────────────────────────────────────────────

function mean(nums) {
  return nums.length ? nums.reduce((a, b) => a + b, 0) / nums.length : null;
}

function median(nums) {
  if (!nums.length) return null;
  const s = [...nums].sort((a, b) => a - b);
  const m = Math.floor(s.length / 2);
  return s.length % 2 ? s[m] : (s[m - 1] + s[m]) / 2;
}

export function summarizeQuestion(question, responses) {
  const values = responses.map((r) => r.answers?.[question.id]).filter((v) => hasAnswer(question, v));
  const answered = values.length;
  const base = { answered, skipped: responses.length - answered };

  switch (question.type) {
    case "single_choice":
    case "dropdown":
    case "multi_choice": {
      const options = [...(question.options || [])];
      if (question.allowOther) options.push({ id: OTHER_ID, label: "Other" });
      const counts = Object.fromEntries(options.map((o) => [o.id, 0]));
      values.forEach((v) => (Array.isArray(v) ? v : [v]).forEach((id) => { if (id in counts) counts[id]++; }));
      const otherTexts = responses
        .map((r) => r.answers?.[`${question.id}${OTHER_ID}`])
        .filter(Boolean);
      const rows = options.map((o) => ({ id: o.id, label: o.label, count: counts[o.id], pct: answered ? (counts[o.id] / answered) * 100 : 0 }));
      const top = [...rows].sort((a, b) => b.count - a.count)[0];
      return { ...base, kind: "choice", rows, otherTexts, top: top?.count ? top : null };
    }
    case "yes_no": {
      const yes = values.filter((v) => v === "yes").length;
      const no = values.filter((v) => v === "no").length;
      return {
        ...base, kind: "binary",
        rows: [
          { id: "yes", label: "Yes", count: yes, pct: answered ? (yes / answered) * 100 : 0 },
          { id: "no", label: "No", count: no, pct: answered ? (no / answered) * 100 : 0 },
        ],
      };
    }
    case "rating":
    case "scale":
    case "nps": {
      const [min, max] = scaleRange(question);
      const nums = values.map(Number).filter((n) => !Number.isNaN(n));
      const dist = [];
      for (let i = min; i <= max; i++) {
        const c = nums.filter((n) => n === i).length;
        dist.push({ value: i, label: String(i), count: c, pct: nums.length ? (c / nums.length) * 100 : 0 });
      }
      const avg = mean(nums);
      const out = { ...base, kind: "scale", min, max, dist, avg, median: median(nums), normalized: avg === null ? null : (avg - min) / (max - min) };
      if (question.type === "nps") {
        const promoters = nums.filter((n) => n >= 9).length;
        const detractors = nums.filter((n) => n <= 6).length;
        const passives = nums.length - promoters - detractors;
        out.nps = nums.length ? Math.round(((promoters - detractors) / nums.length) * 100) : null;
        out.segments = { promoters, passives, detractors };
      }
      return out;
    }
    case "likert": {
      const cols = question.columns || [];
      const rows = (question.rows || []).map((row) => {
        const rowVals = values.map((v) => v?.[row.id]).filter(Boolean);
        const counts = cols.map((c) => rowVals.filter((x) => x === c.id).length);
        const scoreNums = rowVals.map((x) => cols.findIndex((c) => c.id === x) + 1).filter((n) => n > 0);
        const avg = mean(scoreNums);
        return {
          id: row.id, label: row.label, total: rowVals.length, counts,
          pcts: counts.map((c) => (rowVals.length ? (c / rowVals.length) * 100 : 0)),
          avg, normalized: avg === null || cols.length < 2 ? null : (avg - 1) / (cols.length - 1),
        };
      });
      return { ...base, kind: "likert", columns: cols, rows };
    }
    case "number": {
      const nums = values.map(Number).filter((n) => !Number.isNaN(n));
      return { ...base, kind: "number", avg: mean(nums), median: median(nums), min: nums.length ? Math.min(...nums) : null, max: nums.length ? Math.max(...nums) : null, sum: nums.reduce((a, b) => a + b, 0) };
    }
    case "date": {
      const counts = {};
      values.forEach((v) => { counts[v] = (counts[v] || 0) + 1; });
      return { ...base, kind: "date", rows: Object.entries(counts).sort().map(([label, count]) => ({ label, count })) };
    }
    default: {
      const texts = responses
        .map((r) => ({ text: r.answers?.[question.id], response: r }))
        .filter((t) => typeof t.text === "string" && t.text.trim());
      return { ...base, kind: "text", texts, words: topWords(texts.map((t) => t.text)) };
    }
  }
}

const STOP_WORDS = new Set("a an and are as at be been but by can could do does for from had has have i if in into is it its just more most my no not of on or our so some than that the their them then there these they this to too very was we were what when which who will with would you your also should all any about us me am being get more lekoil".split(" "));

export function topWords(texts, limit = 12) {
  const counts = {};
  texts.forEach((t) => {
    String(t).toLowerCase().replace(/[^a-z0-9\s'-]/g, " ").split(/\s+/)
      .filter((w) => w.length > 2 && !STOP_WORDS.has(w))
      .forEach((w) => { counts[w] = (counts[w] || 0) + 1; });
  });
  return Object.entries(counts).sort((a, b) => b[1] - a[1]).slice(0, limit).map(([word, count]) => ({ word, count }));
}

// Plain-text rendering of one answer (tables, drawers, export)
export function formatAnswer(question, answers) {
  const v = answers?.[question.id];
  if (!hasAnswer(question, v)) return "";
  const other = answers?.[`${question.id}${OTHER_ID}`];
  const label = (id) => (id === OTHER_ID ? `Other: ${other || ""}` : question.options?.find((o) => o.id === id)?.label || id);
  switch (question.type) {
    case "single_choice":
    case "dropdown":
      return label(v);
    case "multi_choice":
      return (Array.isArray(v) ? v : [v]).map(label).join("; ");
    case "yes_no":
      return v === "yes" ? "Yes" : "No";
    case "rating":
      return `${v} / ${question.ratingMax || 5}`;
    case "scale":
    case "nps":
      return String(v);
    case "likert":
      return (question.rows || [])
        .map((r) => `${r.label}: ${question.columns?.find((c) => c.id === v[r.id])?.label || "—"}`)
        .join("\n");
    case "date":
      return formatDate(v);
    default:
      return String(v);
  }
}

// ── Formatting ─────────────────────────────────────────────────────────────────

export function formatDate(iso) {
  if (!iso) return "";
  const d = iso.length === 10 ? new Date(iso + "T12:00:00") : new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
}

export function formatDateTime(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString("en-GB", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
}

export function formatDuration(sec) {
  if (!sec) return "—";
  if (sec < 60) return `${Math.round(sec)}s`;
  const m = Math.floor(sec / 60);
  const s = Math.round(sec % 60);
  return m >= 60 ? `${Math.floor(m / 60)}h ${m % 60}m` : `${m}m ${String(s).padStart(2, "0")}s`;
}

export function relativeTime(iso) {
  if (!iso) return "";
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)} min ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)} h ago`;
  if (diff < 86400 * 30) return `${Math.floor(diff / 86400)} d ago`;
  return formatDate(iso);
}

export function respondentLabel(survey, response, index) {
  if (survey?.settings?.anonymous) return `Respondent #${index + 1}`;
  return response.name || response.email;
}

// ── Excel export ───────────────────────────────────────────────────────────────

const HEAD = { font: { bold: true, color: { rgb: "FFFFFF" } }, fill: { fgColor: { rgb: "002D7A" } }, alignment: { vertical: "center", wrapText: true } };

function styleHeader(ws, cols) {
  for (let c = 0; c < cols; c++) {
    const ref = XLSX.utils.encode_cell({ r: 0, c });
    if (ws[ref]) ws[ref].s = HEAD;
  }
}

export function exportSurveyToExcel(survey, responses, assignments, responseByAssignment) {
  const anonymous = !!survey.settings?.anonymous;
  const questions = survey.questions.filter((q) => q.type !== "section");
  const wb = XLSX.utils.book_new();

  // Responses sheet — one row per submission, likert rows expanded to columns
  const header = [...(anonymous ? ["Respondent"] : ["Name", "Email"]), "Department", "Round", "Submitted", "Duration"];
  questions.forEach((q) => {
    if (q.type === "likert") (q.rows || []).forEach((r) => header.push(`${q.title} — ${r.label}`));
    else header.push(q.title);
  });
  const rows = responses.map((r, i) => {
    const row = [...(anonymous ? [`Respondent #${i + 1}`] : [r.name, r.email]), r.department || "", r.round, formatDateTime(r.submittedAt), formatDuration(r.durationSec)];
    questions.forEach((q) => {
      if (q.type === "likert") (q.rows || []).forEach((row2) => row.push(q.columns?.find((c) => c.id === r.answers?.[q.id]?.[row2.id])?.label || ""));
      else {
        const v = r.answers?.[q.id];
        row.push(SCORED_TYPES.includes(q.type) || q.type === "number" ? (hasAnswer(q, v) ? Number(v) : "") : formatAnswer(q, r.answers));
      }
    });
    return row;
  });
  const wsR = XLSX.utils.aoa_to_sheet([header, ...rows]);
  wsR["!cols"] = header.map((h, i) => ({ wch: i < 6 ? 18 : Math.min(48, Math.max(14, h.length * 0.7)) }));
  styleHeader(wsR, header.length);
  XLSX.utils.book_append_sheet(wb, wsR, "Responses");

  // Summary sheet — per question / option breakdown
  const sum = [["Question", "Type", "Answer / Statement", "Count", "Percent", "Average"]];
  questions.forEach((q) => {
    const s = summarizeQuestion(q, responses);
    const type = TYPE_META[q.type]?.label || q.type;
    if (s.kind === "choice" || s.kind === "binary") s.rows.forEach((r, i) => sum.push([i ? "" : q.title, i ? "" : type, r.label, r.count, `${r.pct.toFixed(1)}%`, ""]));
    else if (s.kind === "scale") {
      s.dist.forEach((d, i) => sum.push([i ? "" : q.title, i ? "" : type, d.label, d.count, `${d.pct.toFixed(1)}%`, i ? "" : s.avg?.toFixed(2) ?? ""]));
      if (s.nps !== undefined) sum.push(["", "", "NPS score", s.nps ?? "", "", ""]);
    } else if (s.kind === "likert") s.rows.forEach((r, i) => sum.push([i ? "" : q.title, i ? "" : type, r.label, r.total, "", r.avg?.toFixed(2) ?? ""]));
    else if (s.kind === "number") sum.push([q.title, type, `Min ${s.min ?? "—"} · Max ${s.max ?? "—"} · Median ${s.median ?? "—"}`, s.answered, "", s.avg?.toFixed(2) ?? ""]);
    else sum.push([q.title, type, `${s.answered} written answers`, s.answered, "", ""]);
  });
  const wsS = XLSX.utils.aoa_to_sheet(sum);
  wsS["!cols"] = [{ wch: 50 }, { wch: 16 }, { wch: 44 }, { wch: 9 }, { wch: 10 }, { wch: 10 }];
  styleHeader(wsS, 6);
  XLSX.utils.book_append_sheet(wb, wsS, "Summary");

  // Participation sheet — skipped for anonymous surveys so names never sit beside answers
  if (!anonymous) {
    const part = [["Name", "Email", "Department", "Round", "Shared", "Due", "Status", "Submitted"]];
    assignments.forEach((a) => {
      const resp = responseByAssignment[a.id];
      const st = assignmentStatus(a, resp, survey);
      part.push([a.name, a.email, a.department, a.round, formatDate(a.sharedAt), formatDate(a.dueDate), STATUS_META[st].label, resp ? formatDateTime(resp.submittedAt) : ""]);
    });
    const wsP = XLSX.utils.aoa_to_sheet(part);
    wsP["!cols"] = [{ wch: 26 }, { wch: 32 }, { wch: 18 }, { wch: 7 }, { wch: 14 }, { wch: 14 }, { wch: 12 }, { wch: 20 }];
    styleHeader(wsP, 8);
    XLSX.utils.book_append_sheet(wb, wsP, "Participation");
  }

  const safe = survey.title.replace(/[^a-z0-9]+/gi, "_").slice(0, 60);
  XLSX.writeFile(wb, `${safe}_Results_${todayIso()}.xlsx`);
}
